/**
 * Leads — compact list of website + manually-entered leads.
 *
 * Powered by GET /api/website-leads. The "New lead" button + the
 * `/leads/new` and `/leads?new=1` URLs auto-open NewLeadDialog so
 * QuickActions tile on Dashboard+ has a working target.
 *
 * Phase 16.2 replacement for the prior placeholder shell.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  UserPlus, Plus, Search, MessageCircle, Phone, Mail, MapPin, Calendar, AlertTriangle, X,
  MoreVertical, Share2, Briefcase, Loader2, Tag,
} from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import FollowUpSheet from '@/components/followup/FollowUpSheet';
import { CalendarClock } from 'lucide-react';
import FilterChips from '@/components/list/FilterChips';
import NewLeadDialog from '@/components/lead/NewLeadDialog';
import SendWhatsAppDialog from '@/components/SendWhatsAppDialog';
import WhatsAppSendDialog from '@/components/whatsapp/WhatsAppSendDialog';
import { waNumber } from '@/utils/phone';
import { buildLeadWaContext, leadSuggestHint } from '@/utils/waContext';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return '—'; }
};

const STATUS_PILL = {
  new:        'bg-emerald-50 text-emerald-700 border-emerald-200',
  contacted:  'bg-blue-50 text-blue-700 border-blue-200',
  qualified:  'bg-violet-50 text-violet-700 border-violet-200',
  quoted:     'bg-amber-50 text-amber-700 border-amber-200',
  converted:  'bg-teal-50 text-teal-700 border-teal-200',
  lost:       'bg-rose-50 text-rose-700 border-rose-200',
};

// "Cold" = an active lead (new/contacted) with no contact in 7+ days. We use
// the most recent touch (lastContactedAt → updatedAt → createdAt); a lead with
// no activity timestamp at all is treated as cold. Matches the Dashboard's
// Lead-Health "cold" signal that links here via ?filter=cold.
const COLD_DAYS = 7;
const isColdLead = (l) => {
  const status = (l.status || 'new').toLowerCase();
  if (!['new', 'contacted'].includes(status)) return false;
  const lastTouch = l.lastContactedAt || l.updatedAt || l.createdAt;
  if (!lastTouch) return true;
  const t = new Date(lastTouch).getTime();
  if (isNaN(t)) return true;
  return (Date.now() - t) > COLD_DAYS * 86400 * 1000;
};

const Leads = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [packageFilter, setPackageFilter] = useState('all');
  const [showNew, setShowNew] = useState(() => searchParams.get('new') === '1');
  const isColdFilter = (searchParams.get('filter') || '').toLowerCase() === 'cold';

  const clearColdFilter = () => {
    searchParams.delete('filter');
    setSearchParams(searchParams, { replace: true });
  };

  const fetchLeads = () => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/website-leads?limit=200`, { withCredentials: true })
      .then((res) => setLeads(res.data?.items || []))
      .catch(() => toast.error('Failed to load leads'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchLeads(); }, []);

  useEffect(() => {
    if (searchParams.get('new') === '1') setShowNew(true);
  }, [searchParams]);

  const closeNew = (open) => {
    setShowNew(open);
    if (!open && searchParams.get('new') === '1') {
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const counts = useMemo(() => {
    const c = { all: leads.length, new: 0, contacted: 0, qualified: 0, quoted: 0, converted: 0, lost: 0 };
    for (const l of leads) {
      const s = (l.status || 'new').toLowerCase();
      if (c[s] !== undefined) c[s] += 1;
    }
    return c;
  }, [leads]);

  const chips = [
    { value: 'all',       label: 'All',       count: counts.all },
    { value: 'new',       label: 'New',       count: counts.new },
    { value: 'contacted', label: 'Contacted', count: counts.contacted },
    { value: 'qualified', label: 'Qualified', count: counts.qualified },
    { value: 'quoted',    label: 'Quoted',    count: counts.quoted },
    { value: 'converted', label: 'Converted', count: counts.converted },
    { value: 'lost',      label: 'Lost',      count: counts.lost },
  ];

  const filtered = useMemo(() => {
    let out = leads;
    // Cold filter (from Dashboard ?filter=cold) overrides the status chips.
    if (isColdFilter) {
      out = out.filter(isColdLead);
    } else if (statusFilter !== 'all') {
      out = out.filter((l) => (l.status || 'new').toLowerCase() === statusFilter);
    }
    // Task 7 — source-package (packageCode) attribution filter.
    if (packageFilter !== 'all') {
      out = out.filter((l) => (l.packageCode || '') === packageFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((l) =>
        (l.name || '').toLowerCase().includes(q) ||
        (l.email || '').toLowerCase().includes(q) ||
        (l.phone || '').toLowerCase().includes(q) ||
        (l.destination || '').toLowerCase().includes(q) ||
        (l.packageCode || '').toLowerCase().includes(q) ||
        (l.leadId || '').toLowerCase().includes(q),
      );
    }
    return out;
  }, [leads, statusFilter, search, packageFilter, isColdFilter]);

  const packageOptions = useMemo(() => {
    const set = new Set();
    for (const l of leads) { if (l.packageCode) set.add(l.packageCode); }
    return Array.from(set).sort();
  }, [leads]);

  const coldCount = useMemo(() => leads.filter(isColdLead).length, [leads]);

  return (
    <div className="space-y-4" data-testid="leads-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Leads</h1>
          <p className="text-sm text-slate-500 mt-0.5">{loading ? 'Loading…' : `${leads.length} on file`}</p>
        </div>
        <Button
          onClick={() => setShowNew(true)}
          className="bg-emerald-700 hover:bg-emerald-800 text-white"
          size="sm"
          data-testid="new-lead-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> New lead
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative" style={{ width: 280 }}>
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, email, destination…"
            className="pl-8 h-9 rounded-[7px]"
            data-testid="leads-search"
          />
        </div>
        <FilterChips chips={chips} value={statusFilter} onChange={setStatusFilter} testidPrefix="lead-chip" />
        {packageOptions.length > 0 && (
          <div className="relative inline-flex items-center">
            <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <select
              value={packageFilter}
              onChange={(e) => setPackageFilter(e.target.value)}
              className="pl-8 pr-3 h-9 rounded-[7px] border border-slate-200 text-sm bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              data-testid="leads-package-filter"
            >
              <option value="all">All packages</option>
              {packageOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        )}
      </div>

      {isColdFilter ? (
        <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="cold-filter-banner">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
          <span className="text-amber-800 font-medium">Showing cold leads (no contact in 7+ days)</span>
          <span className="text-amber-600">· {coldCount}</span>
          <button onClick={clearColdFilter} className="ml-auto inline-flex items-center gap-1 text-slate-500 hover:text-rose-600" data-testid="clear-cold-filter">
            <X className="h-3.5 w-3.5" /> Clear filter
          </button>
        </div>
      ) : null}

      {filtered.length === 0 && !loading ? (
        <Card className="p-12 text-center border-dashed border-2 border-slate-200" data-testid="leads-empty">
          <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
            <UserPlus className="h-6 w-6 text-emerald-600" />
          </div>
          <p className="text-sm text-slate-500 mb-1">No leads match the current filter.</p>
          <p className="text-xs text-slate-400">Click <strong>New lead</strong> to add the first one.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((l) => <LeadCard key={l.leadId} l={l} onChanged={fetchLeads} />)}
        </div>
      )}

      <NewLeadDialog
        open={showNew}
        onOpenChange={closeNew}
        onCreated={() => { closeNew(false); fetchLeads(); }}
      />
    </div>
  );
};

const LeadCard = ({ l, onChanged }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const status = (l.status || 'new').toLowerCase();
  const pill = STATUS_PILL[status] || STATUS_PILL.new;
  const waNum = waNumber(l.countryCode, l.phone);
  const [busy, setBusy] = useState(false);
  const [followOpen, setFollowOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [waCloudOpen, setWaCloudOpen] = useState(false);
  const converted = !!l.clientUCC && status === 'converted';

  const convert = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/website-leads/${l.leadId}/convert-traveller`, {}, { withCredentials: true });
      toast.success(r.data?.created ? 'Traveller profile created' : 'Linked to existing traveller');
      onChanged?.();
      navigate(`/clients/${r.data.ucc}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Conversion failed');
    } finally { setBusy(false); }
  };

  const createTrip = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/trips/from-lead/${l.leadId}`, {}, { withCredentials: true });
      toast.success(`Trip ${r.data?.displayTripId || ''} created`);
      navigate(`/quotes/${r.data?.id || r.data?._id}/edit`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create trip');
    } finally { setBusy(false); }
  };

  const shareQuote = async () => {
    setBusy(true);
    try {
      const tr = await axios.get(`${BACKEND_URL}/api/trips/by-lead/${l.leadId}`, { withCredentials: true });
      const trips = Array.isArray(tr.data) ? tr.data : (tr.data?.items || []);
      if (!trips.length) { toast.error('Create a trip first, then build an itinerary to share.'); return; }
      const trip = trips[0];
      const ir = await axios.get(`${BACKEND_URL}/api/itineraries?tripId=${trip.tripId}`, { withCredentials: true });
      const itins = Array.isArray(ir.data) ? ir.data : (ir.data?.items || []);
      const itin = itins[0];
      if (!itin) { toast.error('Build an itinerary for this trip first.'); return; }
      const ref = itin.public_ref || itin.id;
      const url = itin.public_url || `${window.location.origin}/${ref}/preview`;
      window.open(url, '_blank', 'noopener,noreferrer');
      try { await navigator.clipboard.writeText(url); toast.success('Quote opened in a new tab · link copied'); }
      catch { toast.success('Quote opened in a new tab'); }
    } catch (e) {
      toast.error('Could not load the quote link');
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-3 border border-slate-200 hover:shadow-sm transition-shadow" data-testid={`lead-card-${l.leadId}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 truncate">{l.name || '(no name)'}</div>
          <div className="text-[10px] font-mono text-slate-400 mt-0.5">{l.leadId}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${pill} capitalize`}>
            {status}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={busy}
                data-testid={`lead-actions-${l.leadId}`}
                aria-label="Lead actions"
                className="h-7 w-7 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-4 w-4" />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {converted ? (
                <DropdownMenuItem onClick={() => navigate(`/clients/${l.clientUCC}`)} data-testid={`lead-view-traveller-${l.leadId}`}>
                  <UserPlus className="h-4 w-4 mr-2" /> View traveller
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={convert} data-testid={`lead-convert-${l.leadId}`}>
                  <UserPlus className="h-4 w-4 mr-2" /> Convert to traveller
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={createTrip} data-testid={`lead-create-trip-${l.leadId}`}>
                <Briefcase className="h-4 w-4 mr-2" /> Create trip
              </DropdownMenuItem>
              <DropdownMenuItem onClick={shareQuote} data-testid={`lead-share-quote-${l.leadId}`}>
                <Share2 className="h-4 w-4 mr-2" /> Share quote
              </DropdownMenuItem>
              {l.phone && (
                <DropdownMenuItem onClick={() => setWaCloudOpen(true)} data-testid={`lead-send-whatsapp-cloud-${l.leadId}`}>
                  <MessageCircle className="h-4 w-4 mr-2 text-emerald-600" /> WhatsApp — Official <span className="ml-1 text-[10px] text-emerald-600">(tracked)</span>
                </DropdownMenuItem>
              )}
              {l.phone && (
                <DropdownMenuItem onClick={() => setWaOpen(true)} data-testid={`lead-send-whatsapp-${l.leadId}`}>
                  <MessageCircle className="h-4 w-4 mr-2 text-slate-400" /> WhatsApp Web <span className="ml-1 text-[10px] text-slate-400">(not tracked)</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setFollowOpen(true)} data-testid={`lead-schedule-followup-${l.leadId}`}>
                <CalendarClock className="h-4 w-4 mr-2" /> Schedule follow-up
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <FollowUpSheet open={followOpen} onClose={() => setFollowOpen(false)} leadId={l.leadId} />
      {waCloudOpen && (
        <WhatsAppSendDialog open={waCloudOpen} onOpenChange={setWaCloudOpen} contextType="lead" contextId={l.leadId} title={l.name} />
      )}
      <SendWhatsAppDialog
        open={waOpen}
        onOpenChange={setWaOpen}
        title={l.name}
        phone={waNum}
        context={buildLeadWaContext(l, { user })}
        suggestHint={leadSuggestHint(l)}
      />
      {l.packageCode && (
        <div className="text-xs mt-2">
          <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 rounded-full font-medium" data-testid={`lead-package-${l.leadId}`}>
            <Tag className="h-3 w-3" /> {l.packageCode}
          </span>
        </div>
      )}
      {l.destination && (
        <div className="text-xs text-slate-600 mt-2 flex items-center gap-1.5">
          <MapPin className="h-3 w-3 text-slate-400" /> {l.destination}
        </div>
      )}
      {l.travelDate && (
        <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
          <Calendar className="h-3 w-3 text-slate-400" /> {fmtDate(l.travelDate)}
        </div>
      )}
      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-100">
        {l.phone && (
          <>
            <a
              href={`tel:${l.phone}`}
              data-testid={`lead-call-${l.leadId}`}
              className="text-xs text-slate-600 hover:text-emerald-700 inline-flex items-center gap-1"
            >
              <Phone className="h-3 w-3" /> Call
            </a>
            <a
              href={`https://wa.me/${waNum}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`lead-wa-${l.leadId}`}
              className="text-xs text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
            >
              <MessageCircle className="h-3 w-3" /> WhatsApp
            </a>
          </>
        )}
        {l.email && (
          <a
            href={`mailto:${l.email}`}
            data-testid={`lead-mail-${l.leadId}`}
            className="text-xs text-slate-600 hover:text-emerald-700 inline-flex items-center gap-1"
          >
            <Mail className="h-3 w-3" /> Email
          </a>
        )}
        {!converted && (
          <button
            type="button"
            onClick={convert}
            disabled={busy}
            data-testid={`lead-convert-inline-${l.leadId}`}
            className="text-xs text-teal-700 hover:text-teal-900 font-medium ml-auto inline-flex items-center gap-1"
          >
            <UserPlus className="h-3 w-3" /> Convert
          </button>
        )}
      </div>
    </Card>
  );
};

export default Leads;
